import type { SiteCopy } from './types'

/**
 * Verde Vercelli 2 — Italian dictionary.
 *
 * Register: formal «Lei», concrete, no superlatives. Every page opens by
 * naming the entity («Verde Vercelli di Andrea Vetri») and where it works
 * (Vercelli), and every FAQ answer repeats its subject so it still makes
 * sense lifted into a search result or into the FAQPage JSON-LD.
 *
 * The only company figures are the ones today's verdevercelli.it already
 * publishes: dal 2011, 500+ progetti, 4.9 su 30 recensioni. The manifesto
 * statement and lead, the «I nostri valori» words, the services' `name` /
 * `short` and the home FAQ edits of 2026-09-28 are the owner's text; the
 * three values' one-line texts were drafted for the owner's review. Service
 * lists, steps and FAQ answers are new copy (spec §4.6).
 */
export const it = {
  meta: {
    locale: 'it',
    ogLocale: 'it_IT',
    siteName: 'Verde Vercelli',
    legalName: 'Verde Vercelli di Andrea Vetri',
    tagline: 'Manutenzione giardini nel Vercellese',
  },

  a11y: {
    skipToContent: 'Vai al contenuto',
    mainNav: 'Navigazione principale',
    footerNav: 'Navigazione di chiusura pagina',
    langNav: 'Scelta della lingua',
    breadcrumbs: 'Percorso di navigazione',
    openMenu: 'Apri il menu',
    closeMenu: 'Chiudi il menu',
    themeToggle: 'Tema scuro',
    reviews: 'Recensioni dei clienti',
    carouselPrev: 'Precedente',
    carouselNext: 'Successivo',
    carouselGoTo: 'Vai a',
  },

  nav: {
    services: 'Servizi',
    about: 'Chi siamo',
    numbers: 'Numeri',
    areas: 'Zone servite',
    contact: 'Contatti',
    cta: 'Sopralluogo gratuito',
    switchTo: 'English',
    home: 'Home',
  },

  hero: {
    wordmarkSub: 'di Andrea Vetri',
    eyebrow: 'Verde Vercelli di Andrea Vetri · Vercelli',
    title: 'Manutenzione, potature e progettazione di giardini a Vercelli e in provincia.',
    subtitle:
      'Taglio prato, tosatura siepi, abbattimenti e potature in tree-climbing, fresatura dei ceppi e topiarie per privati, condomini e aziende del Vercellese, dal 2011.',
    ctaPrimary: 'Richiedi un sopralluogo gratuito',
    ctaPrimaryShort: 'Sopralluogo gratuito',
    scrollCue: 'Scorri',
  },

  manifesto: {
    eyebrow: 'Chi siamo',
    statement: "Un'azienda orientata verso il *cliente* e le sue *preferenze*.",
    lead:
      "Verde Vercelli di Andrea Vetri è un'impresa di cura e manutenzione del paesaggio con sede a Vercelli, attiva dal 2011 per privati, condomini, aziende e Pubbliche Amministrazioni in tutta la provincia.",
    pillarsTitle: 'I nostri valori',
    pillars: [
      {
        title: 'Affidabilità',
        text: 'Tempi rispettati, lavori eseguiti come concordato e un unico referente dal sopralluogo alla fine del lavoro.',
      },
      {
        title: 'Innovazione',
        text: 'Investiamo ogni anno in formazione, attrezzature e nuove tecniche di lavoro.',
      },
      {
        title: 'Passione',
        text: "Curiamo ogni giardino con l'attenzione che dedicheremmo al nostro.",
      },
    ],
    founderLine: 'Titolare · dal 2011',
  },

  servicesSection: {
    eyebrow: 'Servizi',
    title: 'Cosa facciamo',
    intro:
      'Verde Vercelli di Andrea Vetri segue il verde di Vercelli e della provincia con otto servizi: progettazione giardini, abbattimento piante, taglio prato, pulizia post lavori, tosatura siepi, fresatura dei ceppi, tree-climbing e topiarie artistiche. Una sola squadra e un unico interlocutore, dal sopralluogo alla pulizia finale.',
    more: 'Scopri di più',
  },

  numeri: {
    eyebrow: 'Numeri',
    title: 'Quello che abbiamo fatto',
    years: { label: 'Anni di esperienza', since: 'Dal 2011' },
    projects: { label: 'Progetti completati' },
    reviews: { label: 'Google Reviews', cta: 'Leggi le recensioni' },
  },

  faq: {
    eyebrow: 'Domande frequenti',
    title: 'Le domande che ci fanno più spesso',
    items: [
      {
        q: 'Il sopralluogo è gratuito?',
        a: 'Sì. Il sopralluogo di Verde Vercelli è gratuito a Vercelli e in tutta la provincia: veniamo a vedere il giardino, valutiamo insieme le soluzioni più adeguate per il Suo spazio verde.',
      },
      {
        q: 'Dove lavorate?',
        a: 'Verde Vercelli ha sede a Vercelli e lavora in tutti i comuni del Vercellese, dal Po fino a Gattinara e Lozzolo. In Valsesia lavora il nostro partner Verde Valsesia.',
      },
      {
        q: 'Quando mi ricontattate?',
        a: 'Verde Vercelli ricontatta entro 24 ore lavorative (lunedì–venerdì, 8–18) chi scrive dal sito o su WhatsApp, per fissare insieme la data del sopralluogo gratuito.',
      },
      {
        q: 'Fate anche abbattimenti in spazi stretti?',
        a: "Sì. Quando un albero ad alto fusto sta in un cortile, a ridosso di una casa o in un giardino dove un mezzo non entra, Verde Vercelli lo abbatte in tree-climbing: l'operatore sale sull'albero con corde e imbracatura, lavora da sopra e smonta la pianta sezione per sezione.",
      },
      {
        q: 'Chi smaltisce sfalci e ramaglie?',
        a: "Se ne occupa Verde Vercelli: a fine intervento la squadra raccoglie sfalci, ramaglie e legname, li carica e li porta via, e lascia l'area pulita.",
      },
      {
        q: 'Lavorate con condomini, aziende e Pubbliche Amministrazioni?',
        a: 'Sì. Verde Vercelli cura il verde di privati, condomini, aziende e Pubbliche Amministrazioni a Vercelli e in provincia.',
      },
      {
        q: 'Posso avere un contratto di manutenzione programmata?',
        a: 'Sì. Verde Vercelli propone contratti di manutenzione ordinaria per giardini privati, condomini, aziende e PA a seconda delle esigenze richieste.',
      },
    ],
  },

  contact: {
    eyebrow: 'Contattaci',
    title: 'Parliamo del Suo giardino',
    pageTitle: 'Richiedi un sopralluogo gratuito',
    intro: 'Sopralluogo gratuito a Vercelli e in tutta la provincia.',
    note: 'La ricontattiamo entro 24 ore lavorative (lun–ven, 8–18).',
    formUnavailable: 'Il modulo non è disponibile in questo momento. Ci scriva a',
    form: {},
  },

  zone: {
    eyebrow: 'Zone servite',
    title: 'Vercelli e la sua provincia',
    intro:
      "Verde Vercelli di Andrea Vetri ha sede a Vercelli e lavora in tutto il Vercellese, dal Po fino a Gattinara, per privati, condomini, aziende e Pubbliche Amministrazioni. Qui sotto trova la carta della zona e l'elenco completo dei comuni serviti; la Valsesia è seguita dal nostro partner Verde Valsesia.",
    baseLabel: 'la sede',
    headers: {
      comune: 'Comune',
      note: 'Segni particolari',
    },
    notes: {
      // One checked fact per town (sources: it.wikipedia / official sites, read 2026-09-28),
      // keyed by data/servedComuni.ts; a test requires every key in both locales.
      'albano-vercellese': 'Sede e ingresso del Parco naturale delle Lame del Sesia',
      'alice-castello': "Tombe longobarde dell'VIII secolo e un castello citato già nel 1173",
      arborio: 'Il paese che dà il nome alla varietà di riso Arborio',
      'asigliano-vercellese': "Riso Maratelli, tra le varietà più antiche d'Italia, selezionato qui nel 1914",
      balocco: 'Le piste di prova della Cascina Bella Luigina, ex pista prove Alfa Romeo',
      bianze: "L'antica Blanzate, accampamento romano fortificato con mura spesse 1,5 m",
      'borgo-d-ale': "La pesca Bella di Borgo d'Ale, Presidio Slow Food",
      'borgo-vercelli': 'Ogni 8 settembre al Duomo di Casale, per il voto della peste del 1403',
      buronzo: 'Raro esempio di castello consortile, citato già in un atto del 1039',
      caresana: 'La Corsa dei buoi, la domenica dopo San Giorgio (23 aprile)',
      caresanablot: 'Nel nome, «Blot»: contrazione di Belotto, in uso almeno dal 1744',
      carisio: 'Alle porte della Riserva naturale della Garzaia di Carisio',
      'casale-monferrato': 'Sinagoga del 1595, tra i migliori esempi di sinagoga barocca piemontese',
      'casanova-elvo': 'Castello citato dal 1170, con torre cilindrica e scala a chiocciola',
      cigliano: "La Torre Civica del 1733, eretta sotto la guida dell'architetto Castelli",
      collobiano: 'Castello con torri merlate a coda di rondine e una rara torre ottagonale',
      costanzana: 'Verso Desana il santuario della Madonna delle Grazie, già a inizio Cinquecento',
      crescentino: "Santuario Madonna del Palazzo: qui sorse la prima pieve, voluta da sant'Eusebio",
      crova: 'Riso su oltre il 95% della superficie agraria utilizzabile',
      desana: 'Il «tesoro di Desana»: 51 oggetti preziosi da una villa tardo-romana',
      'fontanetto-po': 'Il Mulino di San Giovanni, ad acqua, sorto dopo lo scavo della roggia nel 1465',
      formigliana: "Citata già nell'882, quando Carlo il Grosso la donò alla Chiesa di Vercelli",
      gattinara: 'Il Gattinara, DOC dal 1967 e DOCG dal 1990, da uve nebbiolo (spanna)',
      ghislarengo: "Nella parrocchiale dell'Assunta, un reliquiario d'argento con il legno della Croce",
      greggio: 'In parte nel Parco naturale delle Lame del Sesia',
      lamporo: 'La cappella della Madonna di Loreto, costruita a scavalco del canale Lamporo',
      lenta: 'Il castello-monastero, con il monastero di benedettine fondato nel 1127',
      lignana: 'Cascina Veneria: qui nel 1949 si girarono le scene principali di «Riso amaro»',
      'livorno-ferraris': 'Dal 1924 porta il nome di Galileo Ferraris, lo scienziato nato qui',
      lozzolo: 'Castello del Duecento: la Sala degli Amici, affrescata da Avondo e amici nel 1866',
      moncrivello: 'Il castello fu la residenza preferita di Iolanda di Francia, che vi morì nel 1478',
      'motta-de-conti': "L'unica frazione, Mantie, sta oltre il Sesia e si raggiunge solo dalla Lomellina",
      novara: "La cupola di San Gaudenzio dell'Antonelli: 121 metri in mattoni e calce, senza ferro",
      olcenengo: 'Vi passa la Via Francigena, da San Germano Vercellese verso Vercelli',
      oldenico: 'Parte del territorio rientra nel Parco naturale delle Lame del Sesia',
      'palazzolo-vercellese': "Patria della csenta, il dolce dell'«incanto delle torte» di fine ottobre",
      pertengo: 'Secondo la tradizione il nome viene dal motto «Pro Te Teneo»',
      pezzana: 'Santuario delle Grazie: pala del Seicento col cardinale Maurizio di Savoia orante',
      prarolo: "Chiesa dell'Assunta, eretta nel 1628 per volere del cardinale Federico Borromeo",
      'quinto-vercellese': 'Santi Nazario e Celso: tra gli affreschi medievali un santo legge con gli occhiali',
      rive: 'Nel 1268 il castello era roccaforte dei ghibellini Tizzoni, al confine col Casalese',
      roasio: "«Il paese con la valigia», emigrato in 50 paesi: Museo dell'emigrante dal 2001",
      ronsecco: 'Santuario dei Viri Veri: il nome storpia in dialetto il latino Villae Veteris',
      rovasenda: 'Il castello del X secolo con la sua torre millenaria',
      salasco: 'Le Selve, set di «Riso amaro» (1949) con Silvana Mangano e Vittorio Gassman',
      'sali-vercellese': 'Il castello, citato dal 1268, forse nato come ricetto per i raccolti',
      saluggia: 'Il paese dei fagioli, con il Re e la Regina del Fagiolo a Carnevale',
      'san-germano-vercellese': 'La cupola della parrocchiale di San Germano, opera del Vittone (1760)',
      'san-giacomo-vercellese': 'Nato da cascine sul torrente Rovasenda, fino al 1964 era Cascine San Giacomo',
      santhia: 'Il Carnevale Storico, il più antico del Piemonte: tracce almeno dal 1328',
      stroppiana: "La Fondazione Marazzato: autocarri d'epoca in un'ex fabbrica tessile",
      tricerro: 'Un nome che ricorda i tre cerri che spiccavano sul luogo',
      trino: "Il Bosco della Partecipanza, concesso nel 1202 a un'associazione di famiglie",
      'tronzano-vercellese': 'Borgo franco dal 1256, nato riunendo i due villaggi di Tronzano',
      vercelli: "Sant'Andrea (1219–1227), tra i primissimi esempi di gotico d'oltralpe in Italia",
      villarboit: 'I due ponti canale con cui il canale Cavour scavalca Rovasenda e Marchiazza',
      villata: 'Borgo sorto da abitanti di Casalvolone in fuga dalle scorrerie tra Vercelli e Novara',
    },
    allProvince: 'Il Vercellese, dal Po a Gattinara',
    allComuniTitle: 'Tutti i comuni serviti',
    partner: { text: 'In Valsesia, da Serravalle Sesia ad Alagna, lavora il nostro partner', link: 'Verde Valsesia' },
    neighbours:
      'Oltre il confine provinciale Verde Vercelli interviene anche a Novara, al di là del Sesia, e a Casale Monferrato, sulla sponda del Po in provincia di Alessandria. Per i comuni non elencati ci scriva: valutiamo la distanza al momento del sopralluogo.',
    home: {
      intro: 'Una sede a Vercelli, una squadra che copre tutto il Vercellese, dal Po a Gattinara.',
      figures: {
        places: 'comuni del Vercellese serviti, da Gattinara al Po',
        area: 'la superficie della provincia di Vercelli',
        rice: 'di risaie nella provincia di Vercelli',
      },
      more: 'Tutti i comuni serviti',
    },
    map: {
      desc: "Carta del Vercellese, dal Po a Gattinara, con la provincia di Vercelli in evidenza entro il suo confine: le risaie campo per campo, i boschi lungo i fiumi, il Canale Cavour, la Baraggia, le Lame del Sesia, il Bosco della Partecipanza e il Parco del Po. Vi sono segnati la sede di Vercelli e tutti i comuni serviti.",
      legendTitle: 'Legenda',
      legend: {
        base: 'Sede operativa',
        comune: 'Comune servito',
        water: 'Fiumi',
        paddy: 'Risaie',
        wood: 'Boschi',
        province: 'Provincia di Vercelli',
      },
      reliefCredit: 'Rilievo: EU-DEM (Copernicus, UE) e SRTM (USGS) via Terrain Tiles',
      osmCredit: 'Uso del suolo, acque e strade © contributori OpenStreetMap',
    },
  },

  footer: {
    addressLabel: 'Sede',
    emailLabel: 'Email',
    phoneLabel: 'Telefono',
    pivaLabel: 'P.IVA',
    reaLabel: 'REA',
    pecLabel: 'PEC',
    hoursLabel: 'Orari',
    reviewsLabel: 'Recensioni',
    network: 'Parte della rete AVMG · Verde Valsesia',
    poweredBy: 'Powered by AVMG',
    links: {
      privacy: 'Privacy',
      cookie: 'Cookie',
      dataDeletion: 'Cancellazione dati',
      credits: 'Crediti',
    },
    socials: {
      instagram: 'Instagram',
      facebook: 'Facebook',
      google: 'Recensioni Google',
      linkedin: 'LinkedIn',
    },
    rights: 'Tutti i diritti riservati',
  },

  notFound: {
    title: 'Pagina non trovata',
    text:
      "La pagina che cercava non esiste più oppure l'indirizzo è stato scritto in modo diverso. Dalla home di Verde Vercelli trova gli otto servizi, le zone servite a Vercelli e in provincia e il modulo per chiedere un sopralluogo.",
    backHome: 'Torna alla home',
  },

  seo: {
    home: {
      title: 'Verde Vercelli — Manutenzione giardini a Vercelli',
      description:
        'Verde Vercelli di Andrea Vetri: progettazione giardini, taglio prato, siepi, abbattimenti in tree-climbing e fresatura ceppi a Vercelli. Dal 2011.',
      ogAlt: 'Veduta aerea di un bosco, fotogramma del video di apertura di Verde Vercelli',
    },
    zone: {
      title: 'Zone servite — Vercelli e provincia | Verde Vercelli',
      description:
        'I comuni serviti da Verde Vercelli: Vercelli, Borgo Vercelli, Trino, Santhià, Crescentino, Gattinara e la provincia, più Novara e Casale Monferrato.',
      ogAlt: 'Veduta aerea di un bosco nel Vercellese, fotogramma del video di Verde Vercelli',
    },
    contatti: {
      title: 'Contatti e sopralluogo gratuito | Verde Vercelli',
      description:
        'Verde Vercelli: sopralluogo gratuito per giardini, potature e abbattimenti a Vercelli e in provincia. La ricontattiamo entro 24 ore lavorative.',
      ogAlt: 'Verde Vercelli — richiesta di sopralluogo gratuito',
    },
    privacy: {
      title: 'Informativa sulla privacy | Verde Vercelli',
      description:
        'Come Verde Vercelli di Andrea Vetri tratta i dati personali raccolti dal sito e dal modulo di contatto, e come esercitare i Suoi diritti.',
      ogAlt: 'Verde Vercelli — informativa sulla privacy',
    },
    cookie: {
      title: 'Cookie policy | Verde Vercelli',
      description:
        'Quali cookie e quali strumenti di terze parti usa il sito di Verde Vercelli, a che cosa servono e come modificare le Sue scelte in qualsiasi momento.',
      ogAlt: 'Verde Vercelli — cookie policy',
    },
    dataDeletion: {
      title: 'Cancellazione dei dati | Verde Vercelli',
      description:
        'Come chiedere a Verde Vercelli la cancellazione dei dati personali raccolti dal sito, dal modulo di contatto o da WhatsApp, e in quanto tempo rispondiamo.',
      ogAlt: 'Verde Vercelli — cancellazione dei dati',
    },
    crediti: {
      title: 'Crediti fotografici e licenze | Verde Vercelli',
      description:
        'Autori, licenze e origine delle fotografie e dei video usati sul sito di Verde Vercelli, insieme alle licenze dei caratteri tipografici.',
      ogAlt: 'Verde Vercelli — crediti fotografici',
    },
    notFound: {
      title: 'Pagina non trovata | Verde Vercelli',
      description:
        'La pagina cercata non esiste sul sito di Verde Vercelli. Dalla home trova i servizi, le zone servite a Vercelli e in provincia e i contatti.',
      ogAlt: 'Verde Vercelli — pagina non trovata',
    },
  },

  pages: {
    progettazione: {
      name: 'Progettazione giardini',
      short:
        'Dal concept alla messa a dimora. Studio del terreno, scelta delle piante, integrazione paesaggistica e impiantistica.',
      heroTitle: 'Progettazione giardini a Vercelli',
      heroIntro:
        "Verde Vercelli di Andrea Vetri progetta giardini nuovi e ripensa quelli esistenti a Vercelli e in provincia: dal concept alla messa a dimora, con lo studio del terreno, la scelta delle piante e l'impiantistica concordate in fase progettuale con il cliente.",
      includesTitle: 'Cosa comprende',
      includes: [
        'Sopralluogo e rilievo per valutare insieme le migliori soluzioni in funzione delle vostre necessità.',
        'Fornitura e posa del robot tagliaerba per agevolare la manutenzione del tuo prato.',
        'Progetto con planimetria ed elenco delle piante, scelte per il suolo e in funzione della semplicità di mantenimento del giardino.',
        "Collaudo degli impianti installati e definizione degli ultimi dettagli per rendere il giardino personalizzato con l'utilizzo di arredi da esterno.",
        'Impianto di irrigazione e drenaggi previsti nel progetto, prima della posa del prato.',
        'Semina o posa di prato naturale a rotolo e primo taglio.',
      ],
      detailTitle: 'Il progetto parte dalle tue esigenze',
      detailText:
        'I giardini da noi realizzati hanno come concept quello di creare uno spazio dedicato al cliente per fare da cornice alla sua casa. Si parte dal considerare le esigenze del cliente, dalla voglia di rinnovare la propria area verde e di adeguarla al meglio in funzione di esposizione, facile gestione futura e sostenibilità.',
      faq: [
        {
          q: 'Quanto tempo serve per decidere un progetto?',
          a: 'Cerchiamo di capire in tempi brevi le scelte migliori al fine di dare una soluzione definitiva ai nostri clienti per poter pianificare la realizzazione compatibilmente con le esigenze del cliente.',
        },
        {
          q: 'Posso tenere le piante che ho già?',
          a: 'Quasi sempre sì: durante il sopralluogo Verde Vercelli valuta ogni pianta esistente e la mantiene nel progetto se è sana e compatibile con il nuovo giardino.',
        },
        {
          q: 'In che stagione conviene realizzare un giardino nuovo?',
          a: 'Verde Vercelli concentra semine e messe a dimora in primavera e in autunno, quando nella pianura vercellese il terreno è lavorabile e le piante hanno il tempo di radicare prima del caldo o del gelo. Le date esatte si fissano nel preventivo.',
        },
      ],
      relatedTitle: 'Servizi collegati',
      ctaLabel: 'Richiedi un sopralluogo gratuito',
      seo: {
        title: 'Progettazione giardini a Vercelli | Verde Vercelli',
        description:
          'Verde Vercelli progetta e realizza giardini a Vercelli e in provincia: studio del terreno, scelta delle piante, irrigazione e messa a dimora.',
        ogAlt: 'Fotogramma del video di Verde Vercelli sulla progettazione giardini',
      },
    },

    abbattimento: {
      name: 'Abbattimento piante',
      short:
        'Abbattimenti in sicurezza, smaltimento del legname. Operazioni in spazi ristretti.',
      heroTitle: 'Abbattimento piante a Vercelli',
      heroIntro:
        "Verde Vercelli di Andrea Vetri abbatte alberi a Vercelli e in provincia anche dove lo spazio è poco: in tree-climbing, sezione per sezione, con i pezzi calati a terra e il legname portato via a fine lavoro. Interveniamo in giardini privati, cortili condominiali e aree aziendali.",
      includesTitle: 'Cosa comprende',
      includes: [
        'Valutazione della pianta e di quello che le sta intorno: tetti, linee aeree, recinzioni, accessi.',
        "Abbattimento per sezioni in tree-climbing, con calata controllata dei pezzi dove non c'è spazio di caduta.",
        'Abbattimento diretto con direzione di caduta pianificata, dove lo spazio lo permette.',
        'Protezione di prato, vialetti e manufatti nella zona di lavoro.',
        'Sezionatura, carico e smaltimento del legname e delle ramaglie.',
        "Fresatura del ceppo, su richiesta, per recuperare l'area.",
      ],
      detailTitle: "Quando l'albero non può cadere intero",
      detailText:
        "Quando un albero ad alto fusto va abbattuto in uno spazio ristretto — un giardino, un cortile, un'area pubblica — non c'è lo spazio per farlo cadere intero e spesso un mezzo non riesce a entrare. Verde Vercelli lavora in tree-climbing: l'operatore sale sull'albero con corde e imbracatura, lavora da sopra e abbatte sezione per sezione, calando ogni pezzo nel punto previsto. Il risultato è un abbattimento senza urti su tetti, recinzioni e piante vicine, e senza solchi nel prato.",
      faq: [
        {
          q: "Serve un'autorizzazione per abbattere un albero?",
          a: "Dipende dal comune e dagli eventuali vincoli sulla pianta o sull'area: diversi comuni del Vercellese disciplinano gli abbattimenti con un proprio regolamento del verde. Verde Vercelli Le indica in sopralluogo che cosa verificare, ma la richiesta resta in capo alla proprietà.",
        },
        {
          q: 'Che cosa succede al legname?',
          a: 'Verde Vercelli seziona il legname, lo carica e lo porta via insieme alle ramaglie a fine intervento. Se preferisce tenere una parte della legna, lo concordiamo prima di iniziare.',
        },
        {
          q: 'Rimuovete anche il ceppo?',
          a: "Su richiesta sì: dopo l'abbattimento Verde Vercelli fresa il ceppo sotto il livello del terreno con una fresatrice dedicata, così l'area si può riseminare o ripiantare.",
        },
      ],
      relatedTitle: 'Servizi collegati',
      ctaLabel: 'Richiedi un sopralluogo gratuito',
      seo: {
        title: 'Abbattimento alberi a Vercelli | Verde Vercelli',
        description:
          'Abbattimento di piante e alberi in tree-climbing a Vercelli e in provincia: Verde Vercelli lavora in spazi ristretti e porta via il legname.',
        ogAlt: "Fotogramma del video di Verde Vercelli sull'abbattimento di una pianta",
      },
    },

    taglioPrato: {
      name: 'Taglio prato',
      short:
        'Tagli del prato, arieggiatura, concimazione e cura programmata su misura per giardini privati, condomini e aree commerciali.',
      heroTitle: 'Taglio prato e cura del prato a Vercelli',
      heroIntro:
        'Verde Vercelli di Andrea Vetri taglia e cura i prati di giardini privati, condomini e aree commerciali a Vercelli e in provincia: tagli a calendario, arieggiatura, concimazione e risemina, con le frequenze decise sul Suo prato.',
      includesTitle: 'Cosa comprende',
      includes: [
        "Taglio del prato con raccolta e asporto dell'erba, alla frequenza concordata.",
        'Rifinitura dei bordi lungo aiuole, vialetti e recinzioni.',
        'Arieggiatura per togliere il feltro e far respirare il terreno.',
        'Concimazioni stagionali calibrate sul prato.',
        'Risemina delle zone diradate o rovinate.',
        'Calendario programmato degli interventi per tutta la stagione.',
      ],
      detailTitle: 'Un prato si cura con il calendario',
      detailText:
        "Nella pianura vercellese le estati sono calde e umide e il prato cresce a ritmi diversi da maggio a settembre: un taglio in ritardo lo stressa, uno troppo basso lo brucia. Verde Vercelli lavora per questo su un calendario concordato a inizio stagione, con la frequenza dei tagli, l'arieggiatura e le concimazioni decise sul Suo prato e non su un ciclo generico. Per condomini e aree commerciali il calendario diventa un contratto di cura programmata con un preventivo unico per l'anno.",
      faq: [
        {
          q: 'Ogni quanto tagliate il prato?',
          a: "La frequenza dei tagli di Verde Vercelli si concorda durante il sopralluogo gratuito e dipende dalla stagione, dall'esposizione e da quanto il prato è irrigato. Le date entrano in un calendario, così sa in anticipo quando passiamo.",
        },
        {
          q: "Portate via l'erba tagliata?",
          a: "Sì. Verde Vercelli raccoglie l'erba a fine intervento e la porta via.",
        },
        {
          q: 'Fate anche tagli singoli?',
          a: 'Sì: Verde Vercelli esegue anche tagli singoli, per esempio dopo un periodo di assenza. Per i prati seguiti tutta la stagione il contratto di cura programmata raccoglie gli interventi in un preventivo unico e fissa le date in anticipo.',
        },
      ],
      relatedTitle: 'Servizi collegati',
      ctaLabel: 'Richiedi un sopralluogo gratuito',
      seo: {
        title: 'Taglio prato a Vercelli | Verde Vercelli',
        description:
          'Taglio prato, arieggiatura, concimazione e cura programmata per privati, condomini e aziende: Verde Vercelli lavora a Vercelli e in provincia.',
        ogAlt: 'Fotogramma del video di Verde Vercelli sul taglio del prato',
      },
    },

    pulizia: {
      name: 'Pulizia post lavori',
      short:
        'Pulizia post-intervento, smaltimento sfalci e ramaglie, ripristino del decoro.',
      heroTitle: 'Pulizia post lavori a Vercelli',
      heroIntro:
        "Verde Vercelli di Andrea Vetri chiude ogni intervento a Vercelli e in provincia con la pulizia dell'area: raccolta e smaltimento di sfalci e ramaglie, pulizia di vialetti e cortili, ripristino del decoro del giardino. Lo stesso servizio è disponibile anche da solo, per esempio per la raccolta delle foglie in autunno.",
      includesTitle: 'Cosa comprende',
      includes: [
        'Raccolta di sfalci, ramaglie, foglie e residui di potatura.',
        'Pulizia con soffiatore di vialetti, cortili, marciapiedi e parcheggi.',
        'Carico, trasporto e smaltimento del materiale di risulta.',
        'Ripristino del decoro: bordi, aiuole e superfici calpestate rimesse in ordine.',
        "Controllo finale dell'area insieme a Lei o al referente del condominio.",
      ],
      detailTitle: "Il lavoro finisce quando l'area è pulita",
      detailText:
        "Una potatura o un abbattimento lasciano sempre qualcosa a terra: foglie, rametti, segatura, erba sui vialetti. Per Verde Vercelli la pulizia fa parte del lavoro e non è un extra da ricordare: la squadra raccoglie tutto, porta via il materiale e ripassa le superfici prima di andare via, così il giardino, il cortile o il parcheggio tornano utilizzabili il giorno stesso. Per questo la proponiamo anche da sola a chi deve rimettere in ordine un'area.",
      faq: [
        {
          q: 'La pulizia è compresa negli interventi?',
          a: "Sì. Ogni intervento di Verde Vercelli si chiude con la raccolta del materiale e la pulizia dell'area.",
        },
        {
          q: 'Fate anche la raccolta delle foglie in autunno?',
          a: 'Sì. Verde Vercelli esegue la raccolta delle foglie a Vercelli e in provincia, come intervento singolo o dentro un contratto di manutenzione programmata, con carico e smaltimento del materiale.',
        },
        {
          q: 'Pulite anche cortili e parcheggi di condomini e aziende?',
          a: "Sì. Verde Vercelli pulisce vialetti, cortili e parcheggi di condomini e aree aziendali dopo ogni intervento sul verde e, su richiesta, a calendario; gli orari si concordano con l'amministratore o con l'ufficio che segue la sede.",
        },
      ],
      relatedTitle: 'Servizi collegati',
      ctaLabel: 'Richiedi un sopralluogo gratuito',
      seo: {
        title: 'Pulizia post lavori e smaltimento verde | Verde Vercelli',
        description:
          'Verde Vercelli raccoglie e smaltisce sfalci, ramaglie e foglie e rimette in ordine giardini, cortili e parcheggi a Vercelli e in provincia.',
        ogAlt: 'Fotogramma del video di Verde Vercelli sulla pulizia a fine lavori',
      },
    },

    siepi: {
      name: 'Tosatura siepi',
      short:
        'Potatura ornamentale e produttiva di siepi, cespugli, alberi da frutto e piante ad alto fusto. Tecniche specifiche per ogni specie.',
      heroTitle: 'Tosatura siepi e potature a Vercelli',
      heroIntro:
        'Verde Vercelli di Andrea Vetri tosa siepi e pota cespugli, alberi da frutto e piante ad alto fusto a Vercelli e in provincia, con la tecnica e il periodo adatti a ogni specie, per giardini privati, condomini e aziende.',
      includesTitle: 'Cosa comprende',
      includes: [
        'Tosatura delle siepi in forma, sui lati e in sommità, con i tagli nel periodo giusto per la specie.',
        'Potatura ornamentale di cespugli e arbusti da fiore.',
        'Potatura produttiva degli alberi da frutto del giardino.',
        'Potatura delle piante ad alto fusto, in tree-climbing dove il cestello non arriva.',
        'Riduzione delle siepi cresciute troppo in altezza o in larghezza.',
        'Raccolta e smaltimento delle ramaglie a fine intervento.',
      ],
      detailTitle: 'Ogni specie ha il suo taglio',
      detailText:
        "Una siepe di lauroceraso, una di ligustro e un melo non si potano allo stesso modo né nello stesso mese: alcune specie fioriscono sul legno dell'anno prima, altre reggono tagli drastici, altre ancora si svuotano alla base se vengono tosate male. Verde Vercelli riconosce le specie in sopralluogo e decide tecnica e periodo pianta per pianta, così la siepe resta fitta dal basso, gli arbusti fioriscono e gli alberi da frutto producono. Per le piante ad alto fusto, dove serve, la potatura si fa in tree-climbing.",
      faq: [
        {
          q: 'Quando si tosano le siepi?',
          a: 'Dipende dalla specie: Verde Vercelli fissa il periodo pianta per pianta, di norma con un taglio principale e una rifinitura nella stagione vegetativa. Le date entrano nel preventivo o nel calendario di manutenzione programmata.',
        },
        {
          q: 'Potate anche gli alberi da frutto?',
          a: 'Sì. Verde Vercelli esegue la potatura produttiva degli alberi da frutto dei giardini di Vercelli e della provincia, nel periodo adatto alla specie, per mantenere la pianta equilibrata e produttiva.',
        },
        {
          q: 'Si può abbassare una siepe cresciuta troppo?',
          a: 'Spesso sì, con una riduzione graduale: Verde Vercelli valuta in sopralluogo quanto la specie tollera il taglio e, se serve, distribuisce la riduzione su più stagioni perché la siepe non resti spoglia.',
        },
      ],
      relatedTitle: 'Servizi collegati',
      ctaLabel: 'Richiedi un sopralluogo gratuito',
      seo: {
        title: 'Tosatura siepi e potature a Vercelli | Verde Vercelli',
        description:
          'Verde Vercelli tosa siepi e pota cespugli, alberi da frutto e piante ad alto fusto a Vercelli e in provincia, con tecniche adatte a ogni specie.',
        ogAlt: 'Fotogramma del video di Verde Vercelli sulla tosatura di una siepe',
      },
    },

    fresatura: {
      name: 'Fresatura dei ceppi',
      short:
        'Rimozione completa dei ceppi con fresatrice professionale. Ripristino del terreno per nuove piantumazioni o utilizzo dello spazio.',
      heroTitle: 'Fresatura dei ceppi a Vercelli',
      heroIntro:
        "Verde Vercelli di Andrea Vetri elimina i ceppi rimasti dopo un abbattimento a Vercelli e in provincia con una fresatrice professionale, che scende sotto il livello del terreno senza aprire uno scavo largo e restituisce l'area a prato, a nuove piantumazioni o ad altro uso.",
      includesTitle: 'Cosa comprende',
      includes: [
        "Verifica dell'area prima di iniziare: sottoservizi, linee di irrigazione, cordoli e muretti vicini.",
        'Fresatura del ceppo sotto il livello del terreno, fino a togliere il colletto e le radici principali affioranti.',
        'Asporto del cippato prodotto dalla fresatura.',
        'Riempimento della buca con terra vegetale e livellamento.',
        'Risemina della zona oppure preparazione per una nuova messa a dimora.',
      ],
      detailTitle: 'Perché il ceppo non va lasciato dove sta',
      detailText:
        "Un ceppo lasciato in giardino intralcia il taglio del prato, trattiene umidità e diventa una porta d'ingresso per funghi e insetti del legno, mentre le radici affioranti continuano a sollevare il terreno. Toglierlo scavando vorrebbe dire aprire una buca larga e far entrare mezzi pesanti; la fresatura di Verde Vercelli lavora invece in verticale su uno spazio ristretto, riduce il ceppo in cippato e lascia l'area pronta per un prato, una nuova pianta o un altro uso dello spazio.",
      faq: [
        {
          q: 'Quanto in profondità scende la fresa?',
          a: "La fresatrice di Verde Vercelli scende sotto il livello del terreno quanto basta a togliere il colletto e le radici principali affioranti. La profondità utile si decide in sopralluogo, in base a che cosa l'area dovrà ospitare dopo.",
        },
        {
          q: 'Si può piantare un nuovo albero al posto del ceppo?',
          a: 'Sì, ma non esattamente nello stesso punto: dopo la fresatura Verde Vercelli riempie con terra vegetale e, se la nuova pianta è un albero, consiglia di spostarla di qualche decina di centimetri, perché nel terreno restano residui legnosi in decomposizione.',
        },
        {
          q: 'La fresatura rovina il prato intorno?',
          a: 'La fresatura di Verde Vercelli lavora su una fascia ristretta attorno al ceppo, quindi il prato circostante resta in gran parte intatto. Dove il passaggio della macchina lo segna, il preventivo prevede la risemina.',
        },
      ],
      relatedTitle: 'Servizi collegati',
      ctaLabel: 'Richiedi un sopralluogo gratuito',
      seo: {
        title: 'Fresatura dei ceppi a Vercelli | Verde Vercelli',
        description:
          "Verde Vercelli fresa i ceppi sotto il livello del terreno e restituisce l'area a prato o a nuove piantumazioni, a Vercelli e in provincia.",
        ogAlt: 'Fotogramma del video di Verde Vercelli sulla fresatura di un ceppo',
      },
    },

    treeClimbing: {
      name: 'Tree-climbing',
      short:
        'Interventi in quota con tecniche di arrampicata su fune. Potature, consolidamenti e ispezioni su alberi di grandi dimensioni.',
      heroTitle: 'Tree-climbing a Vercelli: potature in quota',
      heroIntro:
        "Verde Vercelli di Andrea Vetri interviene sugli alberi di grandi dimensioni a Vercelli e in provincia con la tecnica del tree-climbing: l'operatore sale con corde e imbracatura e lavora dall'interno della chioma, anche in cortili e giardini dove un mezzo non entra.",
      includesTitle: 'Cosa comprende',
      includes: [
        'Ispezione della pianta da terra e dalla chioma: fusto, inserzioni dei rami, cavità e seccume.',
        'Potatura di rimonda, riduzione e alleggerimento della chioma con tagli di ritorno.',
        'Consolidamenti della chioma dove le branche lo richiedono.',
        'Potature di sicurezza sopra tetti, linee, accessi e parcheggi.',
        'Abbattimenti per sezioni con calata controllata dei pezzi.',
        "Raccolta delle ramaglie e pulizia dell'area di lavoro.",
      ],
      detailTitle: "Le competenze dell'alpinismo, applicate agli alberi",
      detailText:
        "Il tree-climbing è un mestiere tecnico che richiede le stesse competenze dell'alpinismo: nodi, ancoraggi, gestione del rischio, lettura del terreno — in questo caso, del legno. Per Verde Vercelli è la tecnica per gli alberi grandi che stanno dove un mezzo non arriva: l'unica cosa che entra in giardino è l'operatore con le corde, e lavorando dall'interno della chioma ogni taglio si fa nel punto giusto. Direzione tecnica a cura di un Istruttore nazionale di Arrampicata libera e Istruttore regionale di Alpinismo.",
      faq: [
        {
          q: 'Il tree-climbing rovina il prato o il vialetto?',
          a: 'No: gli operatori di Verde Vercelli salgono con corde e imbracatura e non portano mezzi pesanti in giardino, quindi prato, vialetti e cordoli non vengono segnati. A terra proteggiamo comunque le superfici su cui caliamo il materiale.',
        },
        {
          q: 'In che periodo si potano gli alberi grandi?',
          a: "Verde Vercelli esegue le potature ordinarie degli alberi nel riposo vegetativo, tra autunno e fine inverno. Gli interventi di sicurezza su rami instabili si fanno invece in qualunque momento dell'anno.",
        },
        {
          q: 'Chi dirige gli interventi in tree-climbing?',
          a: 'Gli interventi in tree-climbing di Verde Vercelli sono diretti da un Istruttore nazionale di Arrampicata libera e Istruttore regionale di Alpinismo, che porta sugli alberi le stesse tecniche di corda, ancoraggio e gestione del rischio della montagna.',
        },
        {
          q: 'Lavorate anche sugli alberi dei condomini?',
          a: "Sì. Verde Vercelli esegue potature e abbattimenti in tree-climbing sulle alberature condominiali di Vercelli e della provincia, con preventivo intestato al condominio e un riepilogo scritto dell'intervento per l'amministratore.",
        },
      ],
      relatedTitle: 'Servizi collegati',
      ctaLabel: 'Richiedi un sopralluogo gratuito',
      seo: {
        title: 'Tree-climbing e potature in quota | Verde Vercelli',
        description:
          'Potature, consolidamenti e abbattimenti in tree-climbing su alberi di grandi dimensioni: Verde Vercelli lavora a Vercelli e in provincia.',
        ogAlt: 'Operatore di Verde Vercelli in tree-climbing su un albero ad alto fusto',
      },
    },

    topiarie: {
      name: 'Topiarie artistiche',
      short:
        'Scultura e modellatura artistica di siepi e arbusti. Forme geometriche, figurative e decorative per giardini di carattere.',
      heroTitle: 'Topiarie artistiche a Vercelli',
      heroIntro:
        'Verde Vercelli di Andrea Vetri modella siepi e arbusti in forme geometriche, figurative e decorative nei giardini di Vercelli e della provincia: dalla formazione di una topiaria nuova al mantenimento di quelle esistenti, con rifilature a calendario.',
      includesTitle: 'Cosa comprende',
      includes: [
        'Scelta della forma e della pianta adatta al punto del giardino.',
        'Formazione di topiarie nuove su arbusti giovani, guidate stagione dopo stagione.',
        'Forme geometriche: sfere, coni, cubi, spirali, siepi squadrate.',
        'Forme figurative e decorative su richiesta.',
        'Recupero della forma di topiarie trascurate.',
        'Rifilature di mantenimento a calendario e raccolta dei residui.',
      ],
      detailTitle: 'Una forma si costruisce nel tempo',
      detailText:
        "Una topiaria non nasce in un pomeriggio: la pianta va guidata per gradi, taglio dopo taglio, finché la forma regge da sola. Verde Vercelli sceglie per ogni forma la specie che la sostiene — foglia piccola, crescita compatta, buona risposta alla potatura — e la modella a mano, senza tagli oltre quello che la pianta tollera. Il risultato dà carattere a un giardino, a un ingresso o a un cortile, e si mantiene con rifilature regolari che mettiamo a calendario insieme alla manutenzione del resto del verde.",
      faq: [
        {
          q: 'Quanto tempo serve per formare una topiaria?',
          a: "Dipende dalla forma e dalla pianta di partenza: Verde Vercelli modella una topiaria per gradi, di solito nell'arco di più stagioni, e Le indica in sopralluogo quanti passaggi prevede prima che la forma sia completa.",
        },
        {
          q: 'Quali piante si prestano alle topiarie?',
          a: "Le specie a foglia piccola e crescita compatta, che rispondono bene alla potatura, come tasso, ligustro, lauroceraso o carpino. Verde Vercelli sceglie la pianta in base alla forma, all'esposizione e al terreno del giardino.",
        },
        {
          q: 'Potete ridare forma a una topiaria trascurata?',
          a: 'Spesso sì: Verde Vercelli valuta in sopralluogo quanto la pianta ha perso la forma e, se la specie lo consente, la recupera con tagli graduali distribuiti su una o più stagioni.',
        },
      ],
      relatedTitle: 'Servizi collegati',
      ctaLabel: 'Richiedi un sopralluogo gratuito',
      seo: {
        title: 'Topiarie artistiche a Vercelli | Verde Vercelli',
        description:
          'Verde Vercelli modella siepi e arbusti in forme geometriche, figurative e decorative per giardini, ingressi e cortili a Vercelli e in provincia.',
        ogAlt: 'Fotogramma del video di Verde Vercelli su una topiaria artistica',
      },
    },
  },
} satisfies SiteCopy
